import {ActionReference, GameObjectReference, GameState, InventoryItems} from "../../../shared/types";
import {html, LitElement, nothing, TemplateResult} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {getState, performAction} from "../services/routeService";
import styles from "../styles/gameCanvasStyles";
import {animateCountdown, animateToTime, formatTime, handleGameOver} from "../utils/gameUtils";
import {postJsonApi} from "../helpers";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = [styles];
    @state()
    private fishingText: string = "";

    private roomImages?: string[];
    @state() private currentText: string = "";
    @state() private isTyping: boolean = false;

    @property({type: Array}) private contentText: string[] = [];
    private contentIndex: number = 0;
    private letterIndex: number = 0;

    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    private countdownIntervalId: ReturnType<typeof setInterval> | undefined;

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    @property({type: String})
    private displayValue = "00:00";
    private defaultAnimateTimer: number = 10 * 60;
    private countdownEndTime: number | null = null;

    @state() private showNumberFeedback: boolean = false;
    @state() private numbers: number[] = Array(6).fill(0);
    @state() private feedback: string[] = [];

    @state() private inventoryButtons: InventoryItems[] = [];
    @state() private selectedInventory: boolean = false;
    @state() private gameCompleted: boolean = false;

    public connectedCallback(): void {
        super.connectedCallback();
        void this.initializeOrResumeCountdown();
        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        try {
            const state: GameState = await getState() as GameState;
            this.updateState(state);
            void this.checkGameState();
            void this.initializeOrResumeCountdown();
        } catch (error) {
            console.error("Failed to refresh state:", error);
        }
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        void this.typeWriter();
        this.requestUpdate();
    }

    private checkForTimerDecrement(state: any): void {
        if (state.decreaseTimer && state.decreaseTimer > 0) {
            this.decreaseTimer(state.decreaseTimer);
        }
    }

    private typeWriter(): void {
        const newContentTextString: string = JSON.stringify(this.contentText);
        const lastContentTextString: string | null = localStorage.getItem("lastContent");

        if (!this.isTyping) {
            if (newContentTextString !== lastContentTextString) {
                localStorage.setItem("lastContent", JSON.stringify(this.contentText));
                this.contentIndex = 0;
                this.letterIndex = 0;
                this.currentText = "";
                this.isTyping = true;

                // Start typing the first piece of content
                this.typeNextLetter();
            } else {
                this.currentText = this.contentText.join("\n\n");
            }
        }
    }

    // Recursive method to type out each letter after a delay
    private typeNextLetter(): void {
        if (this.contentIndex < this.contentText.length) {
            const currentPiece: string = this.contentText[this.contentIndex];

            if (this.letterIndex < currentPiece.length) {
                // Typing out the current letter
                this.currentText += currentPiece.charAt(this.letterIndex++);
                setTimeout(() => this.typeNextLetter(), 25);
            } else {
                // Check if there's another piece of text to type out
                if (this.contentIndex < this.contentText.length - 1) {
                    // If not the last item, add a line break for the next item
                    this.currentText += "\n\n"; // Adds a line break
                }
                // Move to the next piece of text, or finish if there was no new line added
                this.letterIndex = 0;
                this.contentIndex++;
                setTimeout(() => this.typeNextLetter(), 250); // Delay before starting the next piece or finishing
            }
        } else {
            // Typing finished for all pieces, marking typing as complete
            this.isTyping = false;
        }
    }


    private async checkGameState(): Promise<void> {
        const countDownTime: string | null = localStorage.getItem("countdownEndTime");
        const state: GameState | undefined = await getState();
        const now: number = Date.now();

        if (state?.roomAlias && state?.roomAlias !== "game-over" && !countDownTime || (countDownTime && now > parseInt(countDownTime))) {
            void handleGameOver();
            location.reload();
        }
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.alias === "retry-game") {
            localStorage.removeItem("game-started");
            location.reload();
        }

        if (button.alias === "talk:dragonofdoom:4") {
            localStorage.setItem("start-dragon-invaders", String(true));
            location.reload();
        }

        if (button.alias === "enter-code") {
            this.toggleNumberFeedbackVisibility();
        }

        // Resetting the visibility of the number feedback and inventory for other actions
        if (button.alias !== "enter-code" && button.alias !== "check-inventory") {
            this.showNumberFeedback = false;
            this.selectedInventory = false; // Hide inventory unless the specific action is invoked
        }

        if (button.alias === "leave-bomb-room") {
            this.showNumberFeedback = false;
            this.selectedInventory = false; // Ensure inventory is hidden when leaving the room
        }

        if (button.alias === "check-inventory") {
            // Toggle the inventory visibility instead of just setting it to true
            this.selectedInventory = !this.selectedInventory;

            if (this.selectedInventory) { // Only fetch and update inventory if we're showing it
                const state: any = await performAction(button.alias);
                const inventoryItems: any[] = state.inventoryItems;
                this.inventoryButtons = inventoryItems.map(item => ({
                    number: item.value,
                }));
            }
        } else {
            this.selectedInventory = false; // This ensures inventory is hidden when any action other than "check-inv" is performed
        }

        if (button.needsObject) {
            const state: any = await performAction(button.alias);
            this.checkForTimerDecrement(state);

            this.updateState(state);
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();
        } else {
            const state: any = await performAction(button.alias);
            this.checkForTimerDecrement(state);
            this.updateState(state);

            if (button.needsRefresh) {
                await this.refreshState();
                this.updateState(state);
                return;
            }

            this.selectedActionButton = button; // Highlight the current action as selected
        }
        this.requestUpdate(); // Ensure the component updates to reflect the new state
    }

    public delay(): Promise<unknown> {
        const randomMilliseconds: number = Math.floor(Math.random() * 10000);
        return new Promise(resolve => setTimeout(resolve, randomMilliseconds));
    }

    private updateCountdownEndTime(newEndTime: number): void {
        this.countdownEndTime = newEndTime;
        localStorage.setItem("countdownEndTime", newEndTime.toString());
        this.startOrUpdateCountdown();
    }

    private decreaseTimer(decreaseByMinutes: number): void {
        if (this.countdownEndTime !== null) {
            const now: number = Date.now();
            const decreaseBySeconds: number = decreaseByMinutes * 60;
            const currentRemainingSeconds: number = Math.max(Math.floor((this.countdownEndTime - now) / 1000), 0);
            const newEndTime: number = this.countdownEndTime - decreaseBySeconds * 1000;
            this.updateCountdownEndTime(newEndTime);

            const startTime: number = now;
            const startSeconds: number = currentRemainingSeconds;
            const targetSeconds: number = Math.max(currentRemainingSeconds - decreaseBySeconds, 0);

            animateCountdown(startSeconds, startTime, targetSeconds, (displayValue) => {
                this.displayValue = displayValue;
                this.requestUpdate();
            }, () => {
                console.log("Countdown animation complete.");
            });
        }
    }


    private startOrUpdateCountdown(): void {
        if (!this.gameCompleted) {
            // Clear any existing countdown interval to prevent duplicates.
            if (this.countdownIntervalId !== undefined) {
                clearInterval(this.countdownIntervalId);
            }

            const now: number = Date.now();
            if (this.countdownEndTime === null || this.countdownEndTime <= now) {
                // If there's no end time set, or the end time is in the past, do not start a countdown.
                return;
            }

            // Calculate the initial remaining time in seconds and update the display.
            const initialRemainingSeconds: number = Math.max(Math.floor((this.countdownEndTime - now) / 1000), 0);
            const currentSeconds: number = this.convertDisplayValueToSeconds(this.displayValue);
            animateToTime(currentSeconds, Date.now(), initialRemainingSeconds, (displayValue: string) => {
                this.displayValue = displayValue;
                this.requestUpdate();
            }, () => {
                console.log("Animation complete.");
            });

            // Setup a new interval for the countdown.
            this.countdownIntervalId = setInterval(() => {
                const now: number = Date.now();
                const remainingSeconds: number = Math.max(Math.floor((this.countdownEndTime! - now) / 1000), 0);

                if (remainingSeconds <= 0) {
                    clearInterval(this.countdownIntervalId);
                    this.countdownIntervalId = undefined;
                    localStorage.removeItem("countdownEndTime");
                    this.displayValue = formatTime(0);
                    this.requestUpdate();
                    void handleGameOver(); // Ensure this method exists and handles the game over state.
                } else {
                    this.displayValue = formatTime(remainingSeconds);
                    this.requestUpdate();
                }
            }, 1000); // Update every second.
        }
    }


    private convertDisplayValueToSeconds(displayValue: string): number {
        const [minutes, seconds] = displayValue.split(":").map(Number);
        return minutes * 60 + seconds;
    }

    private async initializeOrResumeCountdown(): Promise<void> {
        const storedEndTime: string | null = localStorage.getItem("countdownEndTime");

        if (storedEndTime) {
            const endTime: number = parseInt(storedEndTime, 10);
            const now: number = Date.now();

            if (endTime > now) {
                this.updateCountdownEndTime(endTime);
            } else {
                localStorage.removeItem("countdownEndTime");
                await handleGameOver();
                this.displayValue = formatTime(0);
                this.requestUpdate();
            }
        } else {
            const defaultEndTime: number = Date.now() + this.defaultAnimateTimer * 1000;
            localStorage.setItem("countdownEndTime", defaultEndTime.toString());
            this.updateCountdownEndTime(defaultEndTime);
        }
    }


    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (
            button.alias === "worm-item" ||
            button.alias === "corn-item" ||
            button.alias === "bread-item" ||
            button.alias === "maggot-item" ||
            button.alias === "shitfly-item") {

            this.fishingText = "Fishing.......";
            await this.delay();
            this.fishingText = "";
        }

        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        this.checkForTimerDecrement(state);

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    private async checkNumbers(): Promise<void> {
        const feedback: any = await postJsonApi("check-numbers", {
            numbers: this.numbers,
        });

        if (feedback.status === "success") {
            this.showNumberFeedback = false;
            const gameCompleted: any = await postJsonApi("game/completed", {
                token: localStorage.getItem("jwtToken") ?? null,
                currentTime: Date.now(),
            });
            this.gameCompleted = true;
            clearInterval(this.countdownIntervalId);
            this.updateState(gameCompleted);
        } else if (feedback.status === "game-over") {
            void handleGameOver();
            location.reload();
        }


        if (feedback && feedback.feedback) {
            this.feedback = feedback.feedback;
        } else {
            console.error("Unexpected response from the API");
        }
    }

    private updateNumber(index: number, value: number): void {
        this.numbers[index] = value;
        this.requestUpdate();
    }

    public toggleNumberFeedbackVisibility(): void {
        this.showNumberFeedback = !this.showNumberFeedback;
        this.requestUpdate();
    }

    private getFeedbackClass(index: number): string {
        if (this.feedback[index] === "green") {
            return "correct-feedback";
        } else if (this.feedback[index] === "orange") {
            return "incorrect-feedback";
        } else if (this.feedback[index] === "red") {
            return "wrong-feedback";
        } else {
            return "";
        }
    }

    protected renderNumberFeedback(): TemplateResult | typeof nothing {
        if (!this.showNumberFeedback) return nothing;

        return html`
            <div class="number-feedback">
                ${this.numbers.map((_, index) => html`
                    <input
                        type="number"
                        class="${this.getFeedbackClass(index)}"
                        .value="${this.numbers[index] ?? "0"}"
                        @input="${(e: Event): void => {
                            const input: HTMLInputElement = e.target as HTMLInputElement;
                            if (input.value === "" || isNaN(Number(input.value))) {
                                input.value = "0";
                            }
                            this.updateNumber(index, Number(input.value));
                        }}"
                        placeholder="Number"
                    />`
                )}
            </div>
        `;
    }

    protected render(): TemplateResult {

        return html`
            <div class="game">
                <div class="number">${this.displayValue}</div>
                ${this.renderHeader()}
                ${typeof this.renderNumberFeedback() === "object" ? this.renderNumberFeedback() : this.renderContent()}
                ${this.renderFooter()}
            </div>
        `;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png"/>`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        return html`
            <div class="content">
                ${this.fishingText.length > 0 ? html`<p class="content-text">${this.fishingText}</p>` :
                    html`<p class="content-text">${this.currentText}</p>`}

            </div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map((button) => html`
                            <a class="button ${this.selectedActionButton?.alias === button.alias ? "active" : ""}"
                               tabindex="0"
                               @click=${async (): Promise<void> => {
                                   if (!this.isTyping) await this.handleClickAction(button);
                               }}
                               @keydown=${async (e: KeyboardEvent): Promise<void> => {
                                   if (e.key === "Enter" && !this.isTyping) await this.handleClickAction(button);
                               }}
                            >${button.label}</a>
                        `)}
                    </div>
                    <div>
                        ${this.selectedActionButton ? this.gameObjectButtons?.map((button) => html`
                            <a class="button ${this.selectedGameObjectButtons.has(button) ? "active" : ""}"
                               tabindex="0"
                               @click=${(): void => {
                                   if (!this.isTyping) void this.handleClickObject(button);
                               }}
                               @keydown=${(e: KeyboardEvent): void => {
                                   if (e.key === "Enter" && !this.isTyping) void this.handleClickObject(button);
                               }}
                            >${button.name}</a>
                        `) : nothing}

                        ${this.showNumberFeedback ?
                            html`
                                <a class="button" tabindex="0"
                                   @click=${this.checkNumbers}
                                   @keydown=${(e: KeyboardEvent): void => {
                                       if (e.key === "Enter") void this.checkNumbers();
                                   }}
                                >Try Code!</a>`
                            : nothing
                        }

                        ${this.selectedInventory
                            ? this.inventoryButtons?.map(
                                (button) =>
                                    html`<a class="button" tabindex="0">${button.number}</a>`
                            ) : nothing}
                    </div>
                </div>
            </div>
        `;
    }

}